"use client";

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowLeft, Copy, DoorOpen, Users } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "@/lib/hooks/useAuth";
import { waitForFirebaseUser } from "@/lib/auth/waitForFirebaseUser";
const PENDING_JOIN_KEY = "lesson-test-room-pending-join";

function TestRoomContent() {
  const router = useRouter();
  const { user, loading, isAuthenticated } = useAuth();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [joining, setJoining] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const autoJoinStarted = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setShareUrl(`${window.location.origin}/lessons/test-room`);
    }

    fetch("/api/lessons/test-room")
      .then(res => res.json())
      .then(json => setEnabled(Boolean(json.success && json.enabled)))
      .catch(() => setEnabled(false));
  }, []);

  const handleJoin = useCallback(async () => {
    if (loading) return;

    if (!isAuthenticated) {
      sessionStorage.setItem(PENDING_JOIN_KEY, "1");
      router.push(
        `/auth/login?callbackUrl=${encodeURIComponent("/lessons/test-room")}`
      );
      return;
    }

    setJoining(true);
    try {
      const firebaseUser = await waitForFirebaseUser();
      const idToken = await firebaseUser.getIdToken(true);

      const res = await fetch("/api/lessons/test-room", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      const json = await res.json();

      if (!json.success) {
        toast.error(json.error || "입장에 실패했습니다.");
        return;
      }

      sessionStorage.removeItem(PENDING_JOIN_KEY);
      toast.success("테스트 강의실로 이동합니다.");
      router.push(json.redirectUrl);
    } catch (error) {
      console.error("테스트 강의실 입장 실패:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "입장 중 오류가 발생했습니다."
      );
    } finally {
      setJoining(false);
    }
  }, [isAuthenticated, loading, router]);

  useEffect(() => {
    if (
      autoJoinStarted.current ||
      enabled !== true ||
      loading ||
      !isAuthenticated ||
      joining
    ) {
      return;
    }

    if (sessionStorage.getItem(PENDING_JOIN_KEY) !== "1") {
      return;
    }

    autoJoinStarted.current = true;
    void handleJoin();
  }, [enabled, handleJoin, isAuthenticated, joining, loading]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("링크를 복사했습니다.");
    } catch {
      toast.error("복사에 실패했습니다.");
    }
  };

  if (enabled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-lg font-semibold mb-2">테스트 강의실이 꺼져 있어요</p>
          <p className="text-sm text-gray-400 mb-6">
            Vercel에 <code className="text-violet-300">LESSON_TEST_ROOM_ENABLED=true</code> 를
            설정한 뒤 재배포해 주세요.
          </p>
          <Link href="/" className="text-violet-300 hover:underline text-sm">
            홈으로
          </Link>
        </div>
      </div>
    );
  }

  const joinLabel = joining
    ? "입장 중..."
    : loading
      ? "로그인 확인 중..."
      : isAuthenticated
        ? "테스트 강의실 입장"
        : "로그인 후 입장";

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          홈으로
        </Link>

        <div className="rounded-2xl border border-emerald-500/30 bg-gray-900/80 p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 text-emerald-300" />
            <h1 className="text-xl font-bold">공개 테스트 강의실</h1>
          </div>
          <p className="text-sm text-gray-400 mb-6 leading-relaxed">
            로그인한 누구나 같은 3D 강의실에 동시 입장할 수 있어요. 동시접속·음성·아바타
            테스트용입니다. 초대 코드 없이 아래 링크만 공유하면 됩니다.
          </p>

          <label className="block text-xs text-gray-500 mb-2">공유 링크</label>
          <div className="flex gap-2">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 rounded-xl bg-gray-800 border border-gray-700 px-3 py-2.5 text-sm text-gray-200"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 rounded-xl border border-gray-600 px-3 py-2.5 hover:bg-gray-800 transition"
              aria-label="링크 복사"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={handleJoin}
            disabled={joining || loading}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-4 py-3.5 font-semibold transition"
          >
            <DoorOpen className="w-4 h-4" />
            {joinLabel}
          </button>

          <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
            여러 기기·브라우저에서 같은 링크로 접속해 보세요.
            <br />
            강사 역할은 호스트 계정, 나머지는 학생(게스트)으로 입장합니다.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LessonTestRoomPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-950">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500" />
        </div>
      }
    >
      <TestRoomContent />
    </Suspense>
  );
}
